import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Mail, Wallet, Send, AlertCircle, CheckCircle, Loader2, Key, Users, RefreshCw, Database } from 'lucide-react';
import { Document } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { getEncryptionKey, storeEncryptionKey } from '../../lib/encryption';
import { 
  initializeUserKeyPair, 
  importPublicKey, 
  encryptKeysForRecipient,
  isValidEmail,
  isValidWalletAddress,
  getUserPublicKeyBase64
} from '../../lib/crypto';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import toast from 'react-hot-toast';

interface ShareDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document;
}

interface ShareStep {
  id: string;
  label: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
}

interface RecipientInfo {
  email?: string;
  walletAddress?: string;
  publicKey: string;
  name?: string;
}

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  isOpen,
  onClose,
  document
}) => {
  const [recipient, setRecipient] = useState('');
  const [recipientType, setRecipientType] = useState<'email' | 'wallet'>('email');
  const [isSharing, setIsSharing] = useState(false);
  const [shareSteps, setShareSteps] = useState<ShareStep[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [shareComplete, setShareComplete] = useState(false);
  const [userPublicKey, setUserPublicKey] = useState<string | null>(null);
  const [showKeyMissingError, setShowKeyMissingError] = useState(false);
  const [isRetrievingKeys, setIsRetrievingKeys] = useState(false);
  const [manualKeyInput, setManualKeyInput] = useState({ key: '', iv: '' });
  const [showManualKeyInput, setShowManualKeyInput] = useState(false);

  const { user } = useAuthStore();

  useEffect(() => {
    if (isOpen) {
      initializeSharing();
    }
  }, [isOpen]);

  const initializeSharing = async () => {
    try {
      await initializeUserKeyPair();
      
      const publicKey = await getUserPublicKeyBase64();
      setUserPublicKey(publicKey);
    } catch (error: any) {
      console.error('Failed to initialize sharing:', error);
      toast.error('Failed to initialize secure sharing');
    }
  };

  const initializeShareSteps = () => {
    const steps: ShareStep[] = [
      { id: 'validate', label: 'Validating recipient', status: 'pending' },
      { id: 'keys', label: 'Retrieving encryption keys', status: 'pending' },
      { id: 'encrypt', label: 'Encrypting keys for recipient', status: 'pending' },
      { id: 'store', label: 'Creating secure share record', status: 'pending' },
      { id: 'notify', label: 'Sending notification', status: 'pending' },
      { id: 'complete', label: 'Share completed', status: 'pending' }
    ];
    setShareSteps(steps);
    setCurrentStep(0);
  };

  const updateStepStatus = (stepId: string, status: ShareStep['status'], error?: string) => {
    setShareSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, error } : step
    ));
  };

  const detectRecipientType = (input: string): 'email' | 'wallet' => {
    if (isValidEmail(input)) {
      return 'email';
    } else if (isValidWalletAddress(input)) {
      return 'wallet';
    }
    return 'email';
  };

  const validateRecipient = (input: string): { isValid: boolean; type: 'email' | 'wallet'; error?: string } => {
    const trimmed = input.trim();
    
    if (!trimmed) {
      return { isValid: false, type: 'email', error: 'Recipient is required' };
    }

    if (isValidEmail(trimmed)) {
      return { isValid: true, type: 'email' };
    }

    if (isValidWalletAddress(trimmed)) {
      return { isValid: true, type: 'wallet' };
    }

    return { 
      isValid: false, 
      type: 'email', 
      error: 'Please enter a valid email address or wallet address' 
    };
  };

  const findRecipientInfo = async (recipient: string, type: 'email' | 'wallet'): Promise<RecipientInfo> => {
    try {
      let query = supabase.from('users').select('*');
      
      if (type === 'email') {
        query = query.eq('email', recipient);
      } else {
        query = query.eq('wallet_address', recipient);
      }

      const { data, error } = await query.single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error(`Recipient not found. They need to create an account first.`);
        }
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data.public_key) {
        throw new Error(`Recipient hasn't set up secure sharing yet. They need to log in to initialize their encryption keys.`);
      }

      return {
        email: data.email,
        walletAddress: data.wallet_address,
        publicKey: data.public_key,
        name: data.name || data.email?.split('@')[0] || 'User'
      };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to find recipient');
    }
  };

  const retrieveDocumentKeys = async (documentId: string): Promise<{ key: string; iv: string }> => {
    setIsRetrievingKeys(true);
    try {
      // First, try to get keys from localStorage
      const localKeys = await getEncryptionKey(documentId);
      if (localKeys) {
        return localKeys;
      }

      // If manual key input is provided, use that
      if (showManualKeyInput && manualKeyInput.key && manualKeyInput.iv) {
        // Store the manual keys in localStorage for future use
        await storeEncryptionKey(documentId, manualKeyInput.key, manualKeyInput.iv);
        return {
          key: manualKeyInput.key,
          iv: manualKeyInput.iv
        };
      }

      // If we get here, keys weren't found in localStorage or database
      throw new Error('Document encryption keys not found. This usually happens when:\n\n• You\'re using a different browser/device than where the document was uploaded\n• Your browser\'s local storage was cleared\n• You\'re in incognito/private browsing mode\n\nTo share this document, please access it from the original browser/device where it was uploaded or enter the encryption keys manually.');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to retrieve document encryption keys');
    } finally {
      setIsRetrievingKeys(false);
    }
  };

  const handleShare = async () => {
    if (!user) {
      toast.error('You must be logged in to share documents');
      return;
    }

    const validation = validateRecipient(recipient);
    if (!validation.isValid) {
      toast.error(validation.error || 'Invalid recipient');
      return;
    }

    setIsSharing(true);
    setShareComplete(false);
    setShowKeyMissingError(false);
    initializeShareSteps();

    try {
      // Step 1: Validate recipient
      updateStepStatus('validate', 'processing');
      setCurrentStep(0);
      
      const recipientInfo = await findRecipientInfo(recipient.trim(), validation.type);
      updateStepStatus('validate', 'completed');
      setCurrentStep(1);

      // Step 2: Retrieve document encryption keys
      updateStepStatus('keys', 'processing');
      
      let documentKeys;
      try {
        documentKeys = await retrieveDocumentKeys(document.id);
      } catch (keyError) {
        // If keys not found, show manual key input option
        updateStepStatus('keys', 'error', keyError.message);
        setShowKeyMissingError(true);
        setShowManualKeyInput(true);
        throw keyError;
      }
      
      updateStepStatus('keys', 'completed');
      setCurrentStep(2);

      // Step 3: Encrypt keys for recipient
      updateStepStatus('encrypt', 'processing');
      
      const recipientPublicKey = await importPublicKey(recipientInfo.publicKey);
      const encryptedKeyData = await encryptKeysForRecipient(
        documentKeys.key,
        documentKeys.iv,
        recipientPublicKey
      );
      
      updateStepStatus('encrypt', 'completed');
      setCurrentStep(3);

      // Step 4: Store share record in database
      updateStepStatus('store', 'processing');
      
      const shareRecord = {
        document_id: document.id,
        shared_by: user.id,
        shared_with_email: recipientInfo.email,
        shared_with_wallet: recipientInfo.walletAddress,
        encrypted_key: encryptedKeyData.encryptedKey,
        recipient_key_fingerprint: encryptedKeyData.recipientPublicKeyFingerprint,
        shared_at: new Date().toISOString(),
        access_level: 'read'
      };

      const { error: shareError } = await supabase
        .from('document_shares')
        .insert(shareRecord);

      if (shareError) {
        throw new Error(`Failed to create share record: ${shareError.message}`);
      }

      // Also update the document's shared_with array
      const currentSharedWith = document.sharedWith || [];
      const newSharedWith = recipientInfo.email 
        ? [...currentSharedWith, recipientInfo.email]
        : currentSharedWith;

      const { error: updateError } = await supabase
        .from('documents')
        .update({ 
          shared_with: newSharedWith,
          access_level: 'shared'
        })
        .eq('id', document.id);

      if (updateError) {
        console.warn('Failed to update document shared_with array:', updateError);
      }

      updateStepStatus('store', 'completed');
      setCurrentStep(4);

      // Step 5: Send notification (simulated)
      updateStepStatus('notify', 'processing');
      
      // In a real implementation, you would send an email or push notification
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateStepStatus('notify', 'completed');
      setCurrentStep(5);

      // Step 6: Complete
      updateStepStatus('complete', 'processing');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStepStatus('complete', 'completed');
      
      setShareComplete(true);
      
      toast.success(`Document shared successfully with ${recipientInfo.name || recipient}!`, {
        icon: '🎉',
        duration: 5000
      });

    } catch (error: any) {
      console.error('Share failed:', error);
      
      const currentStepId = shareSteps[currentStep]?.id;
      if (currentStepId && currentStepId !== 'keys') {
        updateStepStatus(currentStepId, 'error', error.message);
      }
      
      // Show special error handling for missing keys
      if (error.message.includes('encryption keys not found')) {
        setShowKeyMissingError(true);
        setShowManualKeyInput(true);
      }
      
      toast.error(error.message || 'Failed to share document');
    } finally {
      setIsSharing(false);
    }
  };

  const handleManualKeyShare = async () => {
    if (!manualKeyInput.key || !manualKeyInput.iv) {
      toast.error('Please enter both encryption key and IV');
      return;
    }

    // Store the manual keys in localStorage
    try {
      await storeEncryptionKey(document.id, manualKeyInput.key, manualKeyInput.iv);
      
      // Reset the form and try sharing again
      setShowManualKeyInput(false);
      setShowKeyMissingError(false);
      
      // Retry sharing with the manual keys
      handleShare();
    } catch (error: any) {
      toast.error(`Failed to store manual keys: ${error.message}`);
    }
  };

  const resetModal = () => {
    setRecipient('');
    setRecipientType('email');
    setIsSharing(false);
    setShareSteps([]);
    setCurrentStep(0);
    setShareComplete(false);
    setShowKeyMissingError(false);
    setShowManualKeyInput(false);
    setManualKeyInput({ key: '', iv: '' });
  };

  const handleClose = () => {
    if (!isSharing) {
      onClose();
      resetModal();
    }
  };

  const getStepIcon = (step: ShareStep) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const validation = validateRecipient(recipient);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <Card className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Share2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Share Document</h3>
                    <p className="text-gray-600">Securely share "{document.name}" with end-to-end encryption</p>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  disabled={isSharing}
                  icon={X}
                />
              </div>

              {/* Security Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                  <Key className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">RSA Encryption</p>
                    <p className="text-sm text-blue-700">2048-bit key exchange</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Zero Knowledge</p>
                    <p className="text-sm text-green-700">Server never sees keys</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                  <Database className="h-6 w-6 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-900">Secure Storage</p>
                    <p className="text-sm text-purple-700">Keys stored in database</p>
                  </div>
                </div>
              </div>

              {/* Manual Key Input */}
              {showManualKeyInput && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <Key className="h-6 w-6 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-yellow-900 mb-2">Enter Encryption Keys Manually</h4>
                      <p className="text-sm text-yellow-800 mb-4">
                        Please enter the encryption key and initialization vector (IV) that were provided when you uploaded this document.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-yellow-800 mb-1">
                            Encryption Key
                          </label>
                          <input
                            type="text"
                            value={manualKeyInput.key}
                            onChange={(e) => setManualKeyInput({...manualKeyInput, key: e.target.value})}
                            placeholder="Enter encryption key..."
                            className="w-full px-3 py-2 border border-yellow-300 rounded-md text-yellow-900 bg-yellow-50 placeholder-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-yellow-800 mb-1">
                            Initialization Vector (IV)
                          </label>
                          <input
                            type="text"
                            value={manualKeyInput.iv}
                            onChange={(e) => setManualKeyInput({...manualKeyInput, iv: e.target.value})}
                            placeholder="Enter IV..."
                            className="w-full px-3 py-2 border border-yellow-300 rounded-md text-yellow-900 bg-yellow-50 placeholder-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        </div>
                        
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => setShowManualKeyInput(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleManualKeyShare}
                            disabled={!manualKeyInput.key || !manualKeyInput.iv}
                            className="flex-1"
                          >
                            Use These Keys
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Key Missing Error */}
              {showKeyMissingError && !showManualKeyInput && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-900 mb-2">Encryption Keys Not Available</h4>
                      <p className="text-sm text-red-800 mb-4">
                        The encryption keys for this document are not available in your current browser session. 
                        This typically happens when:
                      </p>
                      <ul className="list-disc list-inside text-sm text-red-800 space-y-1 mb-4">
                        <li>You're using a different browser or device than where the document was uploaded</li>
                        <li>Your browser's local storage was cleared or you're in incognito mode</li>
                        <li>The document was uploaded from another session</li>
                      </ul>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.location.reload();
                          }}
                          icon={RefreshCw}
                        >
                          Refresh Page
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowManualKeyInput(true);
                            setShowKeyMissingError(false);
                          }}
                          icon={Key}
                        >
                          Enter Keys Manually
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Share Form */}
              {!isSharing && !shareComplete && !showManualKeyInput && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Email or Wallet Address
                    </label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={recipient}
                        onChange={(e) => {
                          setRecipient(e.target.value);
                          setRecipientType(detectRecipientType(e.target.value));
                        }}
                        placeholder="Enter email address or wallet address..."
                        icon={recipientType === 'email' ? Mail : Wallet}
                        error={recipient && !validation.isValid ? validation.error : undefined}
                        className="pr-20"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          recipientType === 'email' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {recipientType === 'email' ? 'Email' : 'Wallet'}
                        </span>
                      </div>
                    </div>
                    {recipient && validation.isValid && (
                      <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Valid {recipientType} address
                      </p>
                    )}
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Important Notes:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>The recipient must have an Aegis Vault account</li>
                          <li>They need to log in at least once to initialize their encryption keys</li>
                          <li>Only the recipient can decrypt and access the shared document</li>
                          <li>You can revoke access at any time from the document settings</li>
                          <li>Encryption keys are now stored securely in the database</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleClose}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleShare}
                      disabled={!validation.isValid}
                      icon={Send}
                      className="flex-1"
                    >
                      Share Securely
                    </Button>
                  </div>
                </div>
              )}

              {/* Share Progress */}
              {isSharing && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Sharing Progress</h4>
                    <div className="space-y-3">
                      {shareSteps.map((step, index) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            step.status === 'completed' 
                              ? 'bg-green-50 border border-green-200' 
                              : step.status === 'processing'
                              ? 'bg-blue-50 border border-blue-200'
                              : step.status === 'error'
                              ? 'bg-red-50 border border-red-200'
                              : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          {getStepIcon(step)}
                          <div className="flex-1">
                            <p className={`font-medium ${
                              step.status === 'error' ? 'text-red-900' : 'text-gray-900'
                            }`}>
                              {step.label}
                            </p>
                            {step.error && (
                              <p className="text-sm text-red-600 mt-1 whitespace-pre-line">{step.error}</p>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Share Complete */}
              {shareComplete && (
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      Document Shared Successfully!
                    </h4>
                    <p className="text-gray-600">
                      {recipient} can now securely access "{document.name}" from their "Shared With Me" section.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShareComplete(false);
                        resetModal();
                      }}
                      className="flex-1"
                    >
                      Share Another
                    </Button>
                    <Button
                      onClick={handleClose}
                      className="flex-1"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareDocumentModal;